import { useDroppable } from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy } from "@dnd-kit/sortable";
import { useEffect, useState } from "react";
import { Icon, Label, Popup } from "semantic-ui-react";

import { DAYPLANNER_COLUMNS } from "modules/Dayplanner";
import { MechanicAppointmentCard } from "modules/Dayplanner/components";
import "modules/Dayplanner/components/MechanicBox/MechanicBox.scss";
import { classNames, getInitials } from "utils/common";

import type { DAY_PLANNER_APPOINTMENT_STATUSES_FILTERS } from "models";
import type { MechanicWithAppointments } from "modules/Dayplanner";
import type { ReactNode } from "react";

type MechanicBoxProps = {
  mechanic: MechanicWithAppointments;
  autoExpand: boolean;
  activeFilters: DAY_PLANNER_APPOINTMENT_STATUSES_FILTERS[];
  mechanicsPinned: number[];
  numberOfCardsPerRow: number;
  draggedOverMechanicId: number | null;
  searchTerm?: string;
  onOver: (mechanicId: number) => void;
  onToggleMechanicPin: (mechamic_id: number) => void;
  onToggleMechanicAutoExpand: (mechanicId: number) => void;
};

type MechanicAppointmentCardsProps = {
  expanded: boolean;
  mechanic: MechanicWithAppointments;
  autoExpanded: boolean;
  numberOfCardsPerRow: number;
  draggedOverMechanicId: number | null;
  onOver: (mechanicId: number) => void;
};

type MechanicPlaceholderProps = {
  order: number;
  mechanic: MechanicWithAppointments;
  onOver: (mechanicId: number) => void;
};

const MAX_APPOINTMENTS = 10;

const MechanicPlaceholder = ({ mechanic, order, onOver }: MechanicPlaceholderProps) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `${mechanic.id}-placeholder-${order}`,
    data: { mechanicId: mechanic.id, order: Math.min(order, mechanic.appointments.length + 1), placeholder: true }
  });

  useEffect(() => {
    if (isOver) onOver(mechanic.id);
  }, [isOver]);

  return (
    <div ref={setNodeRef} className={"MechanicBox-placeholder"}>
      <div className="MechanicBox-placeholder__number">{order}</div>
    </div>
  );
};

const MechanicAppointmentCards = ({ mechanic, numberOfCardsPerRow = 3, expanded, autoExpanded, onOver, draggedOverMechanicId }: MechanicAppointmentCardsProps) => {
  const items: number[] = [];
  const cards: ReactNode[] = [];
  const placeholders: ReactNode[] = [];

  const roundNumber = mechanic.appointments.length % numberOfCardsPerRow === 0;
  const extraSlot = autoExpanded && draggedOverMechanicId && draggedOverMechanicId !== mechanic.id && roundNumber ? 1 : 0;
  const endIndex =
    expanded || autoExpanded
      ? Math.min(MAX_APPOINTMENTS, Math.ceil((mechanic.appointments.length + extraSlot) / numberOfCardsPerRow) * numberOfCardsPerRow)
      : numberOfCardsPerRow;

  for (let i = 1; i <= endIndex; i++) {
    const app = mechanic.appointments.find(app => app.assigned_mechanic_order === i);
    if (app) {
      items.push(app.id);
      cards.push(<MechanicAppointmentCard key={app.id} appointment={app} mechanic={mechanic} column={DAYPLANNER_COLUMNS.Mechanics} onOver={onOver} />);
    } else placeholders.push(<MechanicPlaceholder key={`${mechanic.id}-placeholder-${i}`} mechanic={mechanic} order={i} onOver={onOver} />);
  }

  return (
    <div className="MechanicBox-appointmentCards" style={{ gridTemplateColumns: `repeat(${numberOfCardsPerRow}, 1fr)` }}>
      <SortableContext items={items} strategy={rectSortingStrategy}>
        {cards}
        {placeholders}
      </SortableContext>
    </div>
  );
};

export const MechanicBox = ({
  mechanic,
  mechanicsPinned,
  onToggleMechanicPin,
  numberOfCardsPerRow,
  activeFilters,
  searchTerm,
  onOver,
  onToggleMechanicAutoExpand,
  autoExpand,
  draggedOverMechanicId
}: MechanicBoxProps) => {
  const [expanded, setIsExpanded] = useState(false);
  const { setNodeRef, isOver } = useDroppable({ id: mechanic.id, data: { mechanicId: mechanic.id, order: mechanic.appointments.length > 0 ? null : 1 } });

  useEffect(() => {
    if (isOver) {
      onOver(mechanic.id);
    }
  }, [isOver]);

  const mechanicHasAppointments = !!mechanic.appointments.length;

  const handlePinnedMechanic = (mechanic: MechanicWithAppointments) => {
    onToggleMechanicPin(mechanic.id);
  };

  const toggleExpandedMechanic = (mechanicId: number) => {
    if (autoExpand) {
      setIsExpanded(false);
      onToggleMechanicAutoExpand(mechanicId);
    } else setIsExpanded(!expanded);
  };

  const hasActiveCollapsedAppointments =
    (!!activeFilters.length || !!searchTerm) &&
    !expanded &&
    mechanic.appointments.some(app => !app.inactive && Number(app.assigned_mechanic_order) > numberOfCardsPerRow);

  const extendClasses = classNames({
    "-disabled": !mechanicHasAppointments,
    "-no-pointer": mechanicHasAppointments && mechanic.appointments.length < numberOfCardsPerRow,
    "-border-green": hasActiveCollapsedAppointments
  });

  return (
    <div ref={setNodeRef} className={`MechanicBox ${!mechanicHasAppointments && !isOver && !autoExpand ? "-no-appointments" : ""}`}>
      {(mechanicHasAppointments || autoExpand) && (
        <MechanicAppointmentCards
          mechanic={mechanic}
          numberOfCardsPerRow={numberOfCardsPerRow}
          expanded={expanded}
          autoExpanded={autoExpand}
          onOver={onOver}
          draggedOverMechanicId={draggedOverMechanicId}
        />
      )}

      <div className="MechanicBox-side">
        <div className="MechanicBox-profile">
          <Popup
            trigger={
              mechanic.profile_picture ? (
                <img alt={getInitials(mechanic.first_name, mechanic.last_name)} src={mechanic.profile_picture} />
              ) : (
                <div>{getInitials(mechanic.first_name, mechanic.last_name)}</div>
              )
            }
            content={
              <span className="MechanicBox-name">
                {mechanic.first_name} {mechanic.last_name}
              </span>
            }
          />
        </div>

        {!mechanicHasAppointments && !isOver && !autoExpand && (
          <>
            <span className="mechanic-name">
              {mechanic.first_name || ""} {mechanic.last_name || ""}
            </span>
          </>
        )}

        <div className="MechanicBox-appointments">
          <Label
            className={`extend-appointments-button ${extendClasses}`}
            basic
            onClick={() => mechanic.appointments.length >= numberOfCardsPerRow && toggleExpandedMechanic(mechanic.id)}
          >
            <span>{mechanic.appointments.length}</span>
            {mechanic.appointments.length >= numberOfCardsPerRow && (
              <span className="extend-appointments">
                <Icon className="ellipsis green" />
              </span>
            )}
          </Label>
        </div>
        <div className="MechanicBox-toggle">
          <div className="MechanicBox-pinned">
            <Icon className={mechanicsPinned.includes(mechanic.id) ? "pin" : "circle regular"} onClick={() => handlePinnedMechanic(mechanic)} />
          </div>
        </div>
      </div>
    </div>
  );
};
